import * as React from "react";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { cn } from "@/lib/utils";
import { Skeleton } from "./skeleton";

export interface FormattedDateProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * The date to format. Can be a Date object, ISO string, or timestamp.
   */
  date: Date | string | null | undefined;
  
  /**
   * Format type for the date display.
   * - 'full': Full date and time with timezone (default)
   * - 'date': Date only (no time)
   * - 'time': Time only with timezone
   * - 'relative': Relative time (e.g., "2 hours ago")
   */
  format?: 'full' | 'date' | 'time' | 'relative';
  
  /**
   * Whether to show the timezone abbreviation (e.g., "EST", "PST").
   * Only applies to 'full' and 'time' formats.
   * @default false
   */
  showTimezone?: boolean;
  
  /**
   * Whether to show a loading skeleton while the date is being processed.
   * @default false
   */
  isLoading?: boolean;
  
  /**
   * Custom fallback text to display when date is null/undefined.
   * @default "N/A"
   */
  fallback?: string;
}

/**
 * FormattedDate component displays dates and times in the user's configured timezone.
 * 
 * Features:
 * - Automatic timezone conversion based on user preferences
 * - Multiple format options (full, date, time, relative)
 * - Optional timezone abbreviation display
 * - Error handling for invalid dates
 * - Loading state support
 * - Accessible with proper ARIA labels
 * 
 * @example
 * ```tsx
 * // Full date and time
 * <FormattedDate date={assessment.dueDate} />
 * 
 * // Date only
 * <FormattedDate date={assessment.createdAt} format="date" />
 * 
 * // Relative time
 * <FormattedDate date={notification.timestamp} format="relative" />
 * 
 * // With timezone indicator
 * <FormattedDate date={meeting.scheduledAt} showTimezone />
 * ```
 */
export function FormattedDate({
  date,
  format = 'full',
  showTimezone = false,
  isLoading = false,
  fallback = 'N/A',
  className,
  ...props
}: FormattedDateProps) {
  const {
    formatDate,
    formatDateOnly,
    formatTimeOnly,
    formatRelative,
    getAbbreviation,
    timezone,
  } = useUserTimezone();

  // Show loading skeleton if requested
  if (isLoading) {
    return (
      <Skeleton 
        className={cn("inline-block h-4 w-24", className)} 
        {...props}
      />
    );
  }

  // Handle null/undefined dates
  if (!date) {
    return (
      <span 
        className={cn("text-muted-foreground", className)}
        aria-label="Date not available"
        {...props}
      >
        {fallback}
      </span>
    );
  }

  // Format the date based on the requested format
  let formattedDate: string;
  let ariaLabel: string;
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return (
        <span 
          className={cn("text-destructive", className)}
          aria-label="Invalid date"
          {...props}
        >
          Invalid Date
        </span>
      );
    }

    switch (format) {
      case 'date':
        formattedDate = formatDateOnly(dateObj);
        ariaLabel = `Date: ${formattedDate}`;
        break;
        
      case 'time':
        formattedDate = formatTimeOnly(dateObj);
        if (showTimezone) {
          const tzAbbr = getAbbreviation();
          formattedDate = `${formattedDate}`;
          ariaLabel = `Time: ${formattedDate} in ${timezone}`;
        } else {
          // Remove timezone from display if showTimezone is false
          formattedDate = formattedDate.replace(/\s+[A-Z]{2,5}$/, '');
          ariaLabel = `Time: ${formattedDate}`;
        }
        break;
        
      case 'relative':
        formattedDate = formatRelative(dateObj);
        // For relative format, also include the full date in aria-label for accessibility
        const fullDate = formatDate(dateObj);
        ariaLabel = `${formattedDate} (${fullDate})`;
        break;
        
      case 'full':
      default:
        formattedDate = formatDate(dateObj);
        if (showTimezone) {
          const tzAbbr = getAbbreviation();
          ariaLabel = `Date and time: ${formattedDate} in ${timezone}`;
        } else {
          // Remove timezone from display if showTimezone is false
          formattedDate = formattedDate.replace(/,?\s+[A-Z]{2,5}$/, '');
          ariaLabel = `Date and time: ${formattedDate}`;
        }
        break;
    }

    return (
      <span 
        className={cn(className)}
        aria-label={ariaLabel}
        title={ariaLabel}
        {...props}
      >
        {formattedDate}
      </span>
    );
  } catch (error) {
    console.error('Error formatting date:', error);
    return (
      <span 
        className={cn("text-destructive", className)}
        aria-label="Error displaying date"
        {...props}
      >
        Error displaying date
      </span>
    );
  }
}
